import type { TFunction } from "i18next";

const KNOWN_CODES = [
  "mobile_signing_not_supported",
  "mobile_signing_temporarily_disabled",
  "operation_not_launchable",
  "operation_not_cancelable",
  "operation_expired",
  "signing_not_ready",
  "signing_document_not_found",
  "signing_document_not_prepared",
  "signing_document_not_restartable",
  "document_already_fully_signed",
  "provider_unavailable",
  "provider_rejected_operation",
  "provider_signing_storage_unavailable",
  "provider_cancel_rejected",
  "invitation_cooldown",
  "daily_invitation_limit",
  "recipient_email_not_configured",
  "signing_frontend_not_configured",
  "sigex_notification_feature_disabled",
  "notification_cooldown",
  "daily_notification_limit",
  "resend_cooldown",
  "document_not_pending",
  "document_signed",
  "document_refused",
  "document_revoked",
  "organization_signature_required",
  "revocation_provider_error",
  "invalid_link",
] as const;

type ErrorPayload = {
  error?: { code?: string } | string;
  detail?: string;
  details?: string;
  code?: string;
};

export function getSigningV2ErrorCode(error: unknown): string | null {
  const data = (error as { data?: ErrorPayload })?.data;
  if (!data) return null;
  if (typeof data.error === "object" && data.error?.code) return data.error.code;
  if (typeof data.code === "string") return data.code;
  return null;
}

/**
 * `extractErrorMessage` returns `data.error` verbatim, which for the V2 shape
 * `{ error: { code } }` is an object — rendering it throws. Every
 * `/api/signing/v2/*` response uses that shape, so it needs its own mapper.
 */
export function mapSigningV2Error(
  error: unknown,
  t: TFunction,
  fallbackKey = "signing.egov.errors.unknown",
): string {
  const code = getSigningV2ErrorCode(error);
  if (code) {
    const isKnown = (KNOWN_CODES as readonly string[]).includes(code);
    return isKnown ? t(`signing.egov.errors.${code}`) : t(fallbackKey);
  }

  const data = (error as { data?: ErrorPayload })?.data;
  if (typeof data?.error === "string" && data.error) return data.error;
  if (typeof data?.detail === "string" && data.detail) return data.detail;
  if (typeof data?.details === "string" && data.details) return data.details;
  return t(fallbackKey);
}
